import React from "react";
//Limitless imports
import { log as pilog } from "@limitless/common/utils";
import { useCustomerMessageContext } from "../customer-message/CustomerMessageContext";

// Real Time Enpoint event types
const MESSAGE_EVENT_CLAIM = "claim";
const MESSAGE_EVENT_UNCLAIM = "unclaim";
const MESSAGE_EVENT_NEW_DIALOGUE = "new";
const MESSAGE_EVENT_EXPIRED = "expired";
const MESSAGE_EVENT_REPORTED = "report";
const MESSAGE_EVENT_UNREPORTED = "unreport";
const MESSAGE_EVENT_CLOSED = "close";
const MESSAGE_EVENT_FORWARD = "forward";
const MESSAGE_EVENT_CONFIRMATION = "custConfirmation";
const MESSAGE_EVENT_LIFECYCLE_STATE = "lifecycleState";
const MESSAGE_EVENT_RELEASE = "release";
const MESSAGE_EVENT_UPDATED = "updated";
export const useCustomerWebSockets = () => {
  const [localClient, setLocalClient] = React.useState(null);
  const [subscribedMessageId, setSubscribedMessageId] = React.useState(null);
  const [reconnectionTrigger, setReconnectionTrigger] = React.useState(0);
  const {
    message,
    fetchMessage,
    setTyping,
    clearTyping
  } = useCustomerMessageContext();
  const {
    messageId,
    accessKey
  } = message;
  const log = pilog.getLogger("CustomerWebSockets useCustomerWebSockets");
  React.useEffect(() => {
    log.debug({
      messageId,
      subscribedMessageId
    });
    if (localClient && messageId && subscribedMessageId !== messageId) {
      try {
        const stage = process.env.REACT_APP_WS_STAGE;
        const messageTopic = `flo-public-${stage}-${message.messageId}`;
        localClient.subscribe(messageTopic);
        log.info("Successfully subscribed to message topic: ", messageTopic);
        // if the previousMessageId existed, unsubscribe
        if (subscribedMessageId) {
          localClient.unsubscribe(`flo-public-${stage}-${subscribedMessageId}`);
          log.info("Successfully unsubscribed to topic: ", subscribedMessageId);
        }
        setSubscribedMessageId(messageId);
      } catch (e) {
        log.info("Failed to subscribed to WS", e);
      }
    }
  }, [messageId, localClient, reconnectionTrigger]);
  const processWebSocketEvent = event => {
    if (process.env.REACT_APP_WS_HISTORY) {
      window.wsEvents.push({
        payload: event,
        message
      });
    }

    // Update last sync timestamp when receiving WebSocket events
    localStorage.setItem('lastWebSocketSync', new Date().toISOString());
    switch (event.action) {
      case MESSAGE_EVENT_CLAIM:
        {
          const claimer = event.payload.name ? event.payload.name : "";
          setTyping(claimer, event.payload.userId);
          break;
        }
      case MESSAGE_EVENT_UNCLAIM:
        clearTyping();
        break;
      case MESSAGE_EVENT_UPDATED:
      case MESSAGE_EVENT_NEW_DIALOGUE:
      case MESSAGE_EVENT_RELEASE:
      case MESSAGE_EVENT_LIFECYCLE_STATE:
      case MESSAGE_EVENT_CONFIRMATION:
      case MESSAGE_EVENT_CLOSED:
      case MESSAGE_EVENT_EXPIRED:
      case MESSAGE_EVENT_FORWARD:
      case MESSAGE_EVENT_REPORTED:
        {
          if (messageId && accessKey) {
            fetchMessage(messageId, accessKey);
          }
          break;
        }
      case MESSAGE_EVENT_UNREPORTED:
        {
          const {
            messageId,
            accessKey,
            state,
            escalationAgreed
          } = message;
          if ((state !== "closed" || escalationAgreed) && messageId === event.payload.messageId) {
            fetchMessage(messageId, accessKey);
          }
          break;
        }
      default:
        log.info("Unspported action ", event);
    }
  };
  const onConnect = (client, isReconnect) => {
    if (isReconnect && messageId) {
      //this situation is for people who's network connection has dropped
      fetchMessage(messageId, accessKey);
    }

    // Update last sync timestamp on successful connection
    localStorage.setItem('lastWebSocketSync', new Date().toISOString());
    setLocalClient(client);
  };
  const onDisconnect = client => {
    try {
      // Store disconnection timestamp for missed message recovery
      localStorage.setItem('lastWebSocketDisconnect', new Date().toISOString());
      client.disconnect();
      setLocalClient(null);
    } catch (error) {
      log.info("Failed to disconnect from web sockets %o", error);
    }
  };
  const onReconnect = client => {
    if (client) {
      setLocalClient(client);
    }
    if (message.messageId) {
      // Check if we were disconnected and need to sync missed messages
      const lastDisconnect = localStorage.getItem('lastWebSocketDisconnect');
      const lastSync = localStorage.getItem('lastWebSocketSync');
      if (lastDisconnect && (!lastSync || new Date(lastDisconnect) > new Date(lastSync))) {
        log.info("Syncing messages after reconnection, last disconnect:", lastDisconnect);
        // Fetch current message state to catch any missed updates
        fetchMessage(message.messageId, message.accessKey);
      } else {
        // Regular reconnect without extended outage
        fetchMessage(message.messageId, message.accessKey);
      }

      // Update sync timestamp
      localStorage.setItem('lastWebSocketSync', new Date().toISOString());

      // Force resubscription by clearing subscribedMessageId and triggering useEffect
      setSubscribedMessageId(null);
      setReconnectionTrigger(prev => prev + 1);
    }
  };
  return {
    onConnect,
    onDisconnect,
    onReconnect,
    processWebSocketEvent
  };
};